// components/CustomerNameInput.jsx - Customer Name input with Jobber typeahead
import React, { useState, useEffect, useRef } from 'react'
import { Search, Loader2, MapPin } from 'lucide-react'
import * as api from '../services/api'

/**
 * Customer Name field with a debounced Jobber-client typeahead.
 * - `value` / `onChange(name)` drive the text input (plain manual entry always works).
 * - `onSelectCustomer(customer)` fires when a Jobber client is picked from the dropdown.
 * Degrades silently: if Jobber isn't connected or there are no matches, no dropdown shows.
 */
export default function CustomerNameInput({ value, onChange, onSelectCustomer, tenantId }) {
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const [focused, setFocused] = useState(false)

  const requestIdRef = useRef(0)      // guards against stale responses
  const skipSearchRef = useRef(false) // skip the search caused by a programmatic value change
  const blurTimeoutRef = useRef(null)

  // Debounced search whenever the typed value changes (while focused)
  useEffect(() => {
    // Skip the value change triggered by selecting a customer
    if (skipSearchRef.current) {
      skipSearchRef.current = false
      return
    }

    if (!focused) return

    const term = (value || '').trim()
    if (term.length < 2) {
      setResults([])
      setLoading(false)
      setOpen(false)
      return
    }

    setLoading(true)
    const handle = setTimeout(async () => {
      const reqId = ++requestIdRef.current
      try {
        const data = await api.searchCustomers(tenantId, term)
        if (reqId !== requestIdRef.current) return // stale
        const customers = data.customers || []
        setResults(customers)
        setOpen(customers.length > 0)
      } catch (err) {
        if (reqId !== requestIdRef.current) return
        setResults([])
        setOpen(false)
      } finally {
        if (reqId === requestIdRef.current) setLoading(false)
      }
    }, 300)

    return () => clearTimeout(handle)
  }, [value, focused, tenantId])

  // Cleanup the blur timeout on unmount
  useEffect(() => () => clearTimeout(blurTimeoutRef.current), [])

  const handleSelect = (customer) => {
    skipSearchRef.current = true
    setOpen(false)
    setResults([])
    clearTimeout(blurTimeoutRef.current)
    onSelectCustomer(customer)
  }

  const handleFocus = () => {
    clearTimeout(blurTimeoutRef.current)
    setFocused(true)
    if (results.length > 0) setOpen(true)
  }

  const handleBlur = () => {
    // Delay so a result tap registers before the dropdown closes
    blurTimeoutRef.current = setTimeout(() => {
      setFocused(false)
      setOpen(false)
    }, 150)
  }

  return (
    <div className="relative">
      <div className="relative">
        <input
          type="text"
          placeholder="e.g., John Smith"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={handleFocus}
          onBlur={handleBlur}
          className="form-input pr-10"
          autoFocus
          autoComplete="off"
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Search className="w-5 h-5" />
          )}
        </div>
      </div>

      {open && results.length > 0 && (
        <ul className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-y-auto">
          {results.map((customer) => (
            <li key={customer.id}>
              <button
                type="button"
                // Prevent the input blur from firing before the click registers
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSelect(customer)}
                className="w-full text-left px-4 py-2.5 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
              >
                <div className="font-medium text-gray-900">{customer.name}</div>
                {customer.addresses?.[0]?.formatted && (
                  <div className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                    <MapPin className="w-3 h-3 flex-shrink-0" />
                    <span className="truncate">{customer.addresses[0].formatted}</span>
                    {customer.addresses.length > 1 && (
                      <span className="text-gray-400 flex-shrink-0">
                        +{customer.addresses.length - 1} more
                      </span>
                    )}
                  </div>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
